/**
 * MechanicBingo — 5×5 (or NxN) grid mechanic.
 *
 * Each cell tracks a mixed objective: gameplay event, commerce action,
 * social action. Lines (rows / cols / diagonals) trigger per-line rewards.
 *
 * Slot variants:
 *   - marquee: full grid with abbreviated cell text + "X/25 · Y to next line" status
 *   - tile:    mini grid (color-only cells, no text) + "X/N" type label
 *   - browse:  same grid as marquee at smaller scale, no text
 */

import React from "react";
import "./MechanicBingo.scss";

export type BingoCellState = "empty" | "done" | "line" | "free";

export interface BingoCell {
    id: string;
    label: string;
    state: BingoCellState;
}

interface MechanicBingoProps {
    variant: "marquee" | "tile" | "browse";
    /** Flat list of cells. Length must be `gridSize * gridSize`. */
    cells: BingoCell[];
    /** Grid edge — 5 for 5×5 (default), 3 for 3×3, etc. */
    gridSize?: number;
    /** Optional status footnote shown in marquee state. */
    statusLine?: React.ReactNode;
}

export const MechanicBingo: React.FC<MechanicBingoProps> = ({
    variant, cells, gridSize = 5, statusLine,
}) => {
    const showCellText = variant === "marquee";

    /*
     * A `role="grid"` may not hold gridcells directly — every cell has to sit
     * inside a `role="row"`. Chunk the flat cell list into rows of `gridSize`
     * so the structure exposed to assistive tech matches what the grid role
     * promises. The rows carry the CSS columns (see the SCSS), so the visual
     * layout is unchanged.
     */
    const rows: Array<Array<BingoCell>> = [];
    for (let i = 0; i < cells.length; i += gridSize) {
        rows.push(cells.slice(i, i + gridSize));
    }

    return (
        <div
            className={`mechanic-bingo mechanic-bingo--${variant}`}
            style={{"--mb-grid": gridSize} as React.CSSProperties}
            role="group"
            aria-label={`Bingo card ${cells.filter(c => c.state === "done" || c.state === "line").length} of ${cells.length} filled`}
        >
            <div className="mechanic-bingo__grid" role="grid">
                {rows.map((row, rowIndex) =>
                    <div className="mechanic-bingo__row" role="row" key={`row-${rowIndex}`}>
                        {row.map(cell =>
                            <span
                                key={cell.id}
                                className={`mechanic-bingo__cell is-${cell.state}`}
                                role="gridcell"
                                aria-label={`${cell.label} — ${cell.state}`}
                                title={cell.label}
                            >
                                {showCellText && (cell.state === "free" ? "★ FREE" : cell.label)}
                            </span>)}
                    </div>)}
            </div>

            {variant === "marquee" && statusLine &&
                <div className="mechanic-bingo__status">{statusLine}</div>
            }
        </div>
    );
};
